import * as React from "react";
import { CrudPage, CrudResource } from "../components/CrudPage";
import { Banner } from "../components/States";
import { useAsync } from "../general/useAsync";
import { usePermission } from "../account/AccountStore";
import { Permissions } from "../account/permissions";
import * as Store from "./StructureStore";

/**
 * The academic catalogue, five screens over the same five-verb controller shape.
 *
 * Each is a `CrudResource` declaration handed to CrudPage. The only real variation between them is
 * which parent they hang off, and that shows up as one extra select field plus a lookup for its
 * options.
 *
 * This replaces the scaffolded Razor pages under Pages/Admin/Assessment/** that the old React app
 * linked out to with a bare anchor.
 */

/** Shared shape - every one of these entities is {id, name, description} plus at most one parent. */
interface NamedDraft {
    name: string;
    description: string;
    parentID: string;
}

const nameColumn = <T extends { name?: string }>() => ({
    header: "Name",
    render: (item: T) => <span className="font-medium text-text-strong">{item.name}</span>,
});

const descriptionColumn = <T extends { description?: string }>() => ({
    header: "Description",
    render: (item: T) => item.description || <span className="text-text-weak">&mdash;</span>,
});

function useStructurePermissions() {
    // The hierarchy controllers use the coarse ScadsysCore permission, not the granular
    // assessment:activities.* pair - those govern the assessment tree inside a module, not the catalogue.
    return usePermission(Permissions.STRUCTURE);
}

// ------------------------------------------------------------------ faculties

export const FacultiesScreen: React.FC = () => {
    const canWrite = useStructurePermissions();

    const resource: CrudResource<Store.FacultyAPIModel, NamedDraft> = {
        title: "Faculties",
        icon: ["fad", "building-columns"],
        description: "The top of the academic catalogue. Schools hang off a faculty.",
        singular: "Faculty",
        plural: "Faculties",
        list: Store.listFaculties,
        create: (d) => Store.createFaculty({ name: d.name, description: d.description }),
        update: (id, d) => Store.updateFaculty({ id, name: d.name, description: d.description }),
        remove: Store.deleteFaculty,
        idOf: (f) => f.id ?? "",
        labelOf: (f) => f.name ?? "",
        searchOf: (f) => `${f.name ?? ""} ${f.description ?? ""}`,
        columns: [
            nameColumn(),
            descriptionColumn(),
            { header: "Schools", align: "right", render: (f) => f.schools?.length ?? 0 },
        ],
        fields: [
            { name: "name", label: "Name", required: true, placeholder: "Faculty of Science" },
            { name: "description", label: "Description", type: "textarea" },
        ],
        toDraft: (f) => ({ name: f?.name ?? "", description: f?.description ?? "", parentID: "" }),
        canWrite,
    };

    return <CrudPage resource={resource} />;
};

// -------------------------------------------------------------------- schools

export const SchoolsScreen: React.FC = () => {
    const canWrite = useStructurePermissions();
    const faculties = useAsync(() => Store.listFaculties(), [], { initial: [] });

    const options = React.useMemo(
        () => (faculties.data ?? []).map((f) => ({ value: f.id ?? "", label: f.name ?? "(unnamed)" })),
        [faculties.data]
    );

    const resource: CrudResource<Store.SchoolAPIModel, NamedDraft> = {
        title: "Schools",
        icon: ["fad", "school"],
        description:
            "Academic schools and departments inside a faculty. Not the institution itself - that is the organisation you are signed in to.",
        singular: "School",
        plural: "Schools",
        list: Store.listSchools,
        create: (d) => Store.createSchool({ name: d.name, description: d.description, facultyID: d.parentID || undefined }),
        update: (id, d) => Store.updateSchool({ id, name: d.name, description: d.description, facultyID: d.parentID || undefined }),
        remove: Store.deleteSchool,
        idOf: (s) => s.id ?? "",
        labelOf: (s) => s.name ?? "",
        searchOf: (s) => `${s.name ?? ""} ${s.description ?? ""} ${s.faculty?.name ?? ""}`,
        columns: [
            nameColumn(),
            { header: "Faculty", render: (s) => s.faculty?.name || <span className="text-text-weak">&mdash;</span> },
            descriptionColumn(),
        ],
        fields: [
            { name: "name", label: "Name", required: true, placeholder: "School of Mathematics" },
            { name: "parentID", label: "Faculty", type: "select", options, placeholder: "Unassigned" },
            { name: "description", label: "Description", type: "textarea" },
        ],
        toDraft: (s) => ({ name: s?.name ?? "", description: s?.description ?? "", parentID: s?.facultyID ?? "" }),
        canWrite,
    };

    return (
        <CrudPage resource={resource} deps={[faculties.data]}>
            {faculties.error && <Banner tone="warning" className="mb-4">Faculties could not be loaded, so the faculty picker is empty. {faculties.error}</Banner>}
        </CrudPage>
    );
};

// ------------------------------------------------------------- qualifications

export const QualificationsScreen: React.FC = () => {
    const canWrite = useStructurePermissions();
    const schools = useAsync(() => Store.listSchools(), [], { initial: [] });

    const options = React.useMemo(
        () => (schools.data ?? []).map((s) => ({ value: s.id ?? "", label: s.name ?? "(unnamed)" })),
        [schools.data]
    );

    const resource: CrudResource<Store.QualificationAPIModel, NamedDraft> = {
        title: "Qualifications",
        icon: ["fad", "graduation-cap"],
        description: "Programmes a school offers.",
        singular: "Qualification",
        plural: "Qualifications",
        list: Store.listQualifications,
        create: (d) => Store.createQualification({ name: d.name, description: d.description, schoolID: d.parentID || undefined }),
        update: (id, d) =>
            Store.updateQualification({ id, name: d.name, description: d.description, schoolID: d.parentID || undefined }),
        remove: Store.deleteQualification,
        idOf: (q) => q.id ?? "",
        labelOf: (q) => q.name ?? "",
        searchOf: (q) => `${q.name ?? ""} ${q.description ?? ""} ${q.school?.name ?? ""}`,
        columns: [
            nameColumn(),
            { header: "School", render: (q) => q.school?.name || <span className="text-text-weak">&mdash;</span> },
            descriptionColumn(),
        ],
        fields: [
            { name: "name", label: "Name", required: true, placeholder: "National Senior Certificate" },
            { name: "parentID", label: "School", type: "select", options, placeholder: "Unassigned" },
            { name: "description", label: "Description", type: "textarea" },
        ],
        toDraft: (q) => ({ name: q?.name ?? "", description: q?.description ?? "", parentID: q?.schoolID ?? "" }),
        canWrite,
    };

    return <CrudPage resource={resource} deps={[schools.data]} />;
};

// ------------------------------------------------------- qualification levels

export const QualificationLevelsScreen: React.FC = () => {
    const canWrite = useStructurePermissions();

    const resource: CrudResource<Store.QualificationLevelAPIModel, NamedDraft> = {
        title: "Qualification levels",
        icon: ["fad", "layer-group"],
        description: "Year and grade levels. These are defined once and reused across qualifications.",
        singular: "Qualification level",
        plural: "Qualification levels",
        list: Store.listQualificationLevels,
        create: (d) => Store.createQualificationLevel({ name: d.name, description: d.description }),
        update: (id, d) => Store.updateQualificationLevel({ id, name: d.name, description: d.description }),
        remove: Store.deleteQualificationLevel,
        idOf: (l) => l.id ?? "",
        labelOf: (l) => l.name ?? "",
        searchOf: (l) => `${l.name ?? ""} ${l.description ?? ""}`,
        columns: [
            nameColumn(),
            descriptionColumn(),
            { header: "Program levels", align: "right", render: (l) => l.qualificationPrograms?.length ?? 0 },
        ],
        fields: [
            { name: "name", label: "Name", required: true, placeholder: "Grade 10" },
            { name: "description", label: "Description", type: "textarea" },
        ],
        toDraft: (l) => ({ name: l?.name ?? "", description: l?.description ?? "", parentID: "" }),
        canWrite,
    };

    return <CrudPage resource={resource} />;
};

// -------------------------------------------------------------------- modules

interface ModuleDraft extends NamedDraft {
    credits: string;
}

export const ModulesScreen: React.FC = () => {
    const canWrite = useStructurePermissions();
    const qualifications = useAsync(() => Store.listQualifications(), [], { initial: [] });

    const options = React.useMemo(
        () => (qualifications.data ?? []).map((q) => ({ value: q.id ?? "", label: q.name ?? "(unnamed)" })),
        [qualifications.data]
    );

    // Credits are optional and only matter to credit-counted subject-choice rules (tertiary). Blank means none.
    const credits = (value: string) => (value.trim() === "" ? undefined : Number(value));

    const resource: CrudResource<Store.ModuleAPIModel, ModuleDraft> = {
        title: "Modules",
        icon: ["fad", "book"],
        description:
            "Subjects, independent of any particular year. A module becomes something students are taught once Management pairs it with a program level and a cycle.",
        singular: "Module",
        plural: "Modules",
        list: Store.listModules,
        create: (d) =>
            Store.createModule({
                name: d.name, description: d.description, qualificationID: d.parentID || undefined, credits: credits(d.credits),
            }),
        update: (id, d) => Store.updateModule({
            id, name: d.name, description: d.description, qualificationID: d.parentID || undefined, credits: credits(d.credits),
        }),
        remove: Store.deleteModule,
        idOf: (m) => m.id ?? "",
        labelOf: (m) => m.name ?? "",
        searchOf: (m) => `${m.name ?? ""} ${m.description ?? ""} ${m.qualification?.name ?? ""}`,
        columns: [
            nameColumn(),
            { header: "Qualification", render: (m) => m.qualification?.name || <span className="text-text-weak">&mdash;</span> },
            { header: "Credits", render: (m) => m.credits ?? <span className="text-text-weak">&mdash;</span> },
            descriptionColumn(),
        ],
        fields: [
            { name: "name", label: "Name", required: true, placeholder: "Mathematics" },
            {
                name: "parentID",
                label: "Qualification",
                type: "select",
                options,
                placeholder: "Unassigned",
                hint: "Choose a qualification, or leave unassigned to reuse the module independently.",
            },
            {
                name: "credits",
                label: "Credits",
                type: "number",
                placeholder: "None",
                hint: "Only for curricula that count credits, such as a tertiary programme. Leave blank for school subjects.",
            },
            { name: "description", label: "Description", type: "textarea" },
        ],
        toDraft: (m) => ({
            name: m?.name ?? "",
            description: m?.description ?? "",
            parentID: m?.qualificationID ?? "",
            credits: m?.credits == null ? "" : String(m.credits),
        }),
        canWrite,
    };

    return (
        <CrudPage resource={resource} deps={[qualifications.data]}>
            {qualifications.error && <Banner tone="warning" className="mb-4">Qualifications could not be loaded. {qualifications.error}</Banner>}
        </CrudPage>
    );
};
